import type { Volume } from '../types/content'

export const volumes: Volume[] = [
  {
    icon: '📗',
    number: 'Volume 1',
    title: 'Fundamentals & Foundations',
    lessons: '25 Lessons',
    subtitle:
      'Grip, ready position, court awareness, basic movement, ball control, and core pickleball fundamentals',
    problem:
      'Teaching absolute beginners is one of the most rewarding — and most demanding — parts of being a pickleball instructor. This volume gives you everything you need to guide new players with clarity, patience, and structure, without improvising a single cue.',
    bullets: [
      'How to introduce grip technique — hand placement, paddle angle, pressure points, and common grip variations from the very first session',
      'How to teach the ready position, balanced stance, and proper court setup — foot position, knee bend, weight distribution, and paddle preparation',
      'How to sequence court awareness training — court zones, the kitchen line, positioning fundamentals, and building reliable court habits',
      'How to teach basic serve mechanics — grip, stance, contact point, swing path, ball toss, and introducing consistency before power',
      'How to introduce the return of serve — positioning, preparation, decision-making, and making safe, reliable contact from day one',
      'How to develop ball control and consistency — rally building, contact quality, soft game introduction, and coordination fundamentals',
    ],
    result:
      'Your beginner players feel supported, capable, and motivated to come back — from their very first lesson.',
    popular: true,
  },
  {
    icon: '📘',
    number: 'Volume 2',
    title: 'Strokes & Techniques',
    lessons: '25 Lessons',
    subtitle:
      'Forehand, backhand, volleys, dinking, drives, drop shots, third-shot drops, resets, blocks, and lobs — structured, progressive, and ready to teach at every step',
    problem:
      'Stroke development is the most requested — and most technically demanding — area to teach consistently week after week. This volume gives you 25 fully structured sessions with varied technical focuses, so every lesson feels intentional, progressive, and complete.',
    bullets: [
      'How to develop forehand and backhand fundamentals — paddle preparation, swing path, contact point, follow-through, and direction control',
      'How to teach the dink — soft game mechanics, kitchen positioning, cross-court dinking, and building consistency under pressure',
      'How to sequence the third-shot drop — purpose, mechanics, common errors, and progressions from static practice to live play scenarios',
      'How to introduce resets and blocks — defensive positioning, absorbing pace, neutralizing attacks, and transitioning back to neutral',
      'How to build volleys and overhead mechanics — punch volleys, swing volleys, overhead footwork, contact point, and shot selection at the kitchen',
    ],
    result:
      'You teach every core pickleball stroke with confidence and consistency — and your players feel clear, measurable progress every single week.',
  },
  {
    icon: '📙',
    number: 'Volume 3',
    title: 'Strategy & Tactics',
    lessons: '25 Lessons',
    subtitle:
      'Shot selection, court awareness, point construction, offensive and defensive decision-making, kitchen strategy, and reading opponents',
    problem:
      'Teaching strategy and tactical awareness requires precise cues, smooth progressions, and a clear sense of building understanding from simple concepts to live match situations. This volume removes all planning pressure and gives you sessions that flow naturally from concept introduction to match application.',
    bullets: [
      'How to teach shot selection as a skill — when to drive, when to drop, when to reset, and how to recognize the right opportunity',
      'How to develop court awareness and positioning — offensive and defensive zones, transition positioning, and controlling the kitchen line',
      'How to introduce point construction — building points with intention, creating pressure, moving opponents, and recognizing patterns',
      'How to teach kitchen strategy — controlling the pace, dinking with purpose, forcing attackable balls, and neutralizing opponents at the net',
      'How to build tactical decision-making — percentage pickleball, risk vs. reward, reading opponents, and making smarter choices under match pressure',
    ],
    result:
      "Your players don't just hit the ball correctly — they understand the game and make better decisions every single point.",
  },
  {
    icon: '📕',
    number: 'Volume 4',
    title: 'Doubles Play',
    lessons: '25 Lessons',
    subtitle:
      'Doubles positioning, partner movement, court coverage, communication, stacking, offensive and defensive formations, and coordinated match strategy',
    problem:
      'Teaching doubles requires a completely different kind of preparation: longer skill sequences, partner coordination, communication under pressure, and strategic thinking as a unit. This volume gives you everything structured so you can focus entirely on your players.',
    bullets: [
      'How to teach doubles positioning — court coverage, partner spacing, protecting the middle, and moving as a coordinated unit',
      'How to develop partner movement — transition as a team, moving to the kitchen together, switching, and covering each other’s positions',
      'How to introduce stacking concepts — when and why to stack, how to execute it cleanly, and how to teach it progressively',
      'How to build offensive formations — creating pressure together, coordinating attacks, and using positioning to force errors',
      'How to teach doubles strategy and communication — pre-point planning, calling shots, supporting your partner, and building a competitive doubles mindset',
    ],
    result:
      'Your players learn to function as a true unit — not just two individuals sharing the same side of the court.',
  },
  {
    icon: '📒',
    number: 'Volume 5',
    title: 'Drills & Skills',
    lessons: '25 Lessons',
    subtitle:
      'Structured skill development through specific drills for serving, returns, dinking, volleys, third shots, resets, footwork, accuracy, consistency, and competitive scenarios',
    problem:
      'Converting technical and tactical concepts into real, usable skills requires structured repetition, clear objectives, and smart activity design. This volume eliminates the constant need to search for new drills and gives you 25 complete sessions focused on systematic skill development.',
    bullets: [
      'How to structure serving and return drills — accuracy targets, consistency challenges, pressure variations, and progressive difficulty',
      'How to build dinking and soft game drills — cross-court patterns, kitchen control, consistency circuits, and competitive dinking scenarios',
      'How to develop footwork and movement drills — court coverage patterns, reaction speed work, split-step timing, and transition footwork',
      'How to create competitive skill scenarios — point construction challenges, pressure situations, scoring systems, and drills that simulate match conditions',
      'How to sequence drills for doubles coordination — partner communication drills, movement patterns, coverage exercises, and team-based competitive activities',
    ],
    result:
      'Technical and tactical concepts become real, repeatable skills that your players can apply under actual match conditions.',
  },
  {
    icon: '📓',
    number: 'Volume 6',
    title: 'Coaching & Instruction',
    lessons: '25 Lessons',
    subtitle:
      'Lesson organization, session structure, coaching communication, teaching progressions, error detection, corrective feedback, player engagement, and professional development',
    problem:
      'This is the volume that transforms sport content into a real teaching system. It’s not just about pickleball — it’s about becoming a clearer, more effective, and more confident instructor in every session you deliver.',
    bullets: [
      'How to organize lessons and structure sessions — warm-up sequencing, teaching progressions, drill selection, and pacing a session from start to finish',
      'How to deliver coaching cues effectively — simple language, visual demonstrations, timing your feedback, and avoiding information overload',
      'How to detect errors and deliver corrective feedback — observation frameworks, identifying root causes, choosing the right correction, and knowing when to stay quiet',
      'How to manage private lessons, small groups, and clinics — adapting your approach, managing different levels, keeping everyone engaged',
      'How to build player confidence and connect drills to match play — creating sessions that feel purposeful, motivating, and genuinely developmental',
    ],
    result:
      'You teach with greater clarity, structure, and confidence — and your players feel the difference in every single session.',
  },
]
